import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type ContratoTipo = 'PRESTACAO_SERVICOS' | 'FORNECIMENTO' | 'LOCACAO' | 'OUTRO';

export interface Contrato {
  id: string;
  workspaceId: string;
  acordoId: string | null;
  tipo: ContratoTipo;
  numero: string | null;
  contratadoCpfCnpj: string;
  contratadoNome: string;
  objeto: string;
  valorTotal: string;
  dataInicio: string;
  dataFim: string | null;
  r2Key: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ContratosListResult {
  items: Contrato[];
  total: number;
  limit: number;
  offset: number;
}

export interface ContratosAggregate {
  ativos: number;
  total: number;
  valorTotal: string;
  vencendoEm30Dias: number;
  porTipo: Record<string, { count: number; total: string }>;
}

export interface ContratoFilters {
  tipo?: ContratoTipo;
  search?: string;
  dataInicio?: string;
  dataFim?: string;
  limit?: number;
  offset?: number;
}

export interface CreateContratoInput {
  tipo: ContratoTipo;
  numero?: string;
  contratadoCpfCnpj: string;
  contratadoNome: string;
  objeto: string;
  valorTotal: string;
  dataInicio: string;
  dataFim?: string;
  r2Key?: string;
  acordoId?: string;
}

function basePath(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/contratos`;
}

function buildQuery(filters: ContratoFilters): string {
  const q = new URLSearchParams();
  if (filters.tipo) q.set('tipo', filters.tipo);
  if (filters.search) q.set('search', filters.search);
  if (filters.dataInicio) q.set('dataInicio', filters.dataInicio);
  if (filters.dataFim) q.set('dataFim', filters.dataFim);
  if (filters.limit != null) q.set('limit', String(filters.limit));
  if (filters.offset != null) q.set('offset', String(filters.offset));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function useContratos(workspaceId: string | null | undefined, filters: ContratoFilters) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['contratos', workspaceId, filters],
    queryFn: () => api.get<ContratosListResult>(`${basePath(workspaceId!)}/${buildQuery(filters)}`),
  });
}

export function useContratosAggregate(workspaceId: string | null | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['contratos', workspaceId, 'aggregate'],
    queryFn: () => api.get<ContratosAggregate>(`${basePath(workspaceId!)}/aggregate`),
  });
}

export function useCreateContrato(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContratoInput) =>
      api.post<Contrato>(`${basePath(workspaceId!)}/`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contratos', workspaceId] });
    },
  });
}

export function useDeleteContrato(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`${basePath(workspaceId!)}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contratos', workspaceId] });
    },
  });
}
